/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DSE.REGISTRY — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DseRegistryPage.tsx. A DISTINCT
 * presentation (drill-down card lists + bottom-sheet detail/edit) over the SAME
 * data/logic layer only:
 *   - reads via apiDb (dse_entity + dse_attribute_def, same columns the desktop
 *     selects — no invented columns),
 *   - entity enable / view-rebuild go through the sanctioned RPCs
 *     (dse_enable_entity / dse_rebuild_entity) exactly like the desktop page,
 *   - every attribute-schema write (add / edit / delete) routes through the
 *     Common Routine via routedInsert / routedUpdate / routedDelete — §2 single
 *     write path; never a direct apiDb write.
 * It never re-renders the desktop two-panel grid; §16.
 *
 * There are no FK/_tl lookups on these tables: entity_name and attribute_key are
 * natural identifiers (table/column names), and every coded value (data_type,
 * unknown_key_policy, is_*) is a CHECK-enum whose label is localised through
 * t() labelled constants (§17.B11) — the surrogate ids (dse_entity_id /
 * dse_attribute_def_id) never reach render (§17.B4). Errors-only messaging (§17.B7).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Search, ChevronLeft, Inbox, X, SquarePen, Plus, RefreshCw,
  Trash2, Database, KeyRound,
} from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DSE.REGISTRY';

// ---------------------------------------------------------------------------
// Domain types (mirror the desktop DseRegistryPage row shapes exactly).
// ---------------------------------------------------------------------------

type DataType = 'STRING' | 'INT' | 'NUMBER' | 'BOOL' | 'DATE' | 'DATETIME' | 'ENUM';
type Policy = 'ALLOW' | 'REJECT';

interface EntityRow {
  dse_entity_id: number;
  entity_name: string;
  unknown_key_policy: Policy;
  is_enabled: 'Y' | 'N';
  field_count: number;
}

interface AttrRow {
  dse_attribute_def_id: number;
  entity_name: string;
  attribute_key: string;
  label: string | null;
  data_type: DataType;
  is_required: 'Y' | 'N';
  format_name: string | null;
  regex: string | null;
  allowed_values: unknown;
  min_value: number | null;
  max_value: number | null;
  min_len: number | null;
  max_len: number | null;
  is_indexed: 'Y' | 'N';
  sort_order: number;
  is_active: 'Y' | 'N';
}

// CHECK-enum label constants — value is the stored code, label is localised (§17.B11).
const DATA_TYPES: { value: DataType; labelKey: string; fallback: string }[] = [
  { value: 'STRING', labelKey: 'mobile.dse.type.STRING', fallback: 'Text' },
  { value: 'INT', labelKey: 'mobile.dse.type.INT', fallback: 'Integer' },
  { value: 'NUMBER', labelKey: 'mobile.dse.type.NUMBER', fallback: 'Number' },
  { value: 'BOOL', labelKey: 'mobile.dse.type.BOOL', fallback: 'Boolean' },
  { value: 'DATE', labelKey: 'mobile.dse.type.DATE', fallback: 'Date' },
  { value: 'DATETIME', labelKey: 'mobile.dse.type.DATETIME', fallback: 'Date & time' },
  { value: 'ENUM', labelKey: 'mobile.dse.type.ENUM', fallback: 'Enum' },
];

const POLICIES: { value: Policy; labelKey: string; fallback: string }[] = [
  { value: 'ALLOW', labelKey: 'mobile.dse.policy.ALLOW', fallback: 'Allow unknown keys' },
  { value: 'REJECT', labelKey: 'mobile.dse.policy.REJECT', fallback: 'Reject unknown keys' },
];

const ATTR_SELECT =
  'dse_attribute_def_id, entity_name, attribute_key, label, data_type, is_required, ' +
  'format_name, regex, allowed_values, min_value, max_value, min_len, max_len, ' +
  'is_indexed, sort_order, is_active';

function typeLabel(t: TFunction, dt: DataType): string {
  const found = DATA_TYPES.find((x) => x.value === dt);
  return found ? t(found.labelKey, found.fallback) : dt;
}

function policyLabel(t: TFunction, p: Policy): string {
  const found = POLICIES.find((x) => x.value === p);
  return found ? t(found.labelKey, found.fallback) : p;
}

function leadForType(dt: DataType): MobileLeadTone {
  switch (dt) {
    case 'INT':
    case 'NUMBER':
      return 'blue';
    case 'DATE':
    case 'DATETIME':
      return 'green';
    case 'BOOL':
      return 'amber';
    case 'ENUM':
      return 'blue';
    default:
      return 'slate';
  }
}

const isNumericType = (dt: DataType): boolean => dt === 'INT' || dt === 'NUMBER';

// ---------------------------------------------------------------------------
// Top-level screen — drills from the entity list into an entity's attributes.
// ---------------------------------------------------------------------------

type EntityFacet = 'all' | 'enabled' | 'disabled';
type AttrFacet = 'all' | 'active' | 'inactive';

export function MobileDseRegistryPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();

  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [attrs, setAttrs] = useState<AttrRow[]>([]);

  const [entFilter, setEntFilter] = useState('');
  const [entFacet, setEntFacet] = useState<EntityFacet>('all');
  const [attrFilter, setAttrFilter] = useState('');
  const [attrFacet, setAttrFacet] = useState<AttrFacet>('all');
  const [indexedOnly, setIndexedOnly] = useState(false);

  const [selectedAttr, setSelectedAttr] = useState<AttrRow | null>(null);
  const [editing, setEditing] = useState<AttrRow | null>(null);
  const [enableOpen, setEnableOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reloadEntities = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawEnts, error: eErr } = await apiDb
        .from('dse_entity')
        .select('dse_entity_id, entity_name, unknown_key_policy, is_enabled')
        .order('entity_name');
      if (eErr) throw eErr;

      // Roll up how many shadow-fields each entity defines — a scannable summary
      // of the desktop's per-entity attribute grid.
      const { data: rawAttrs, error: aErr } = await apiDb
        .from('dse_attribute_def')
        .select('entity_name');
      if (aErr) throw aErr;
      const counts: Record<string, number> = {};
      for (const a of (rawAttrs ?? []) as { entity_name: string }[]) {
        counts[a.entity_name] = (counts[a.entity_name] ?? 0) + 1;
      }

      type RawEnt = { dse_entity_id: number; entity_name: string; unknown_key_policy: Policy; is_enabled: 'Y' | 'N' };
      const rows: EntityRow[] = ((rawEnts ?? []) as RawEnt[]).map((e) => ({
        dse_entity_id: e.dse_entity_id,
        entity_name: e.entity_name,
        unknown_key_policy: e.unknown_key_policy,
        is_enabled: e.is_enabled,
        field_count: counts[e.entity_name] ?? 0,
      }));
      setEntities(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reloadAttrs = async (entity: string) => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dse_attribute_def')
        .select(ATTR_SELECT)
        .eq('entity_name', entity)
        .order('sort_order')
        .order('attribute_key');
      if (e) throw e;
      setAttrs((data ?? []) as AttrRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reloadEntities(); }, []);
  useEffect(() => { if (selectedEntity) void reloadAttrs(selectedEntity); }, [selectedEntity]);

  const filteredEntities = useMemo(() => {
    const q = entFilter.trim().toLowerCase();
    return entities.filter((e) => {
      if (entFacet === 'enabled' && e.is_enabled !== 'Y') return false;
      if (entFacet === 'disabled' && e.is_enabled !== 'N') return false;
      if (!q) return true;
      return e.entity_name.toLowerCase().includes(q);
    });
  }, [entities, entFilter, entFacet]);

  const filteredAttrs = useMemo(() => {
    const q = attrFilter.trim().toLowerCase();
    return attrs.filter((a) => {
      if (attrFacet === 'active' && a.is_active !== 'Y') return false;
      if (attrFacet === 'inactive' && a.is_active !== 'N') return false;
      if (indexedOnly && a.is_indexed !== 'Y') return false;
      if (!q) return true;
      return (
        a.attribute_key.toLowerCase().includes(q) ||
        (a.label ?? '').toLowerCase().includes(q)
      );
    });
  }, [attrs, attrFilter, attrFacet, indexedOnly]);

  const kpis = useMemo(() => ({
    fields: attrs.length,
    active: attrs.filter((a) => a.is_active === 'Y').length,
    indexed: attrs.filter((a) => a.is_indexed === 'Y').length,
    required: attrs.filter((a) => a.is_required === 'Y').length,
  }), [attrs]);

  const openEntity = (entity: string) => {
    setSelectedEntity(entity);
    setAttrFilter('');
    setAttrFacet('all');
    setIndexedOnly(false);
    setError(null);
  };

  const backToEntities = () => {
    setSelectedEntity(null);
    setAttrs([]);
    setSelectedAttr(null);
    setError(null);
    void reloadEntities();
  };

  const addField = async () => {
    if (!selectedEntity) return;
    setActionBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedInsert(session, 'dse_attribute_def', {
      entity_name: selectedEntity,
      attribute_key: `field_${Date.now()}`,
      data_type: 'STRING',
      is_required: 'N',
      is_indexed: 'N',
      is_active: 'Y',
    });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    void reloadAttrs(selectedEntity);
  };

  const rebuild = async () => {
    if (!selectedEntity) return;
    setActionBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'dse_rebuild_entity',
        { p_entity: selectedEntity },
      );
      if (e) throw e;
      if (data && data.ok === false) {
        setError(data.error_code ?? t('mobile.dse.rebuild_failed', 'Rebuild failed.'));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const deleteAttr = async (a: AttrRow) => {
    setActionBusy(true);
    setError(null);
    const { error: e } = await routedDelete(session, 'dse_attribute_def', 'dse_attribute_def_id', a.dse_attribute_def_id);
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelectedAttr(null);
    setAttrs((prev) => prev.filter((x) => x.dse_attribute_def_id !== a.dse_attribute_def_id));
  };

  const constraintText = (a: AttrRow): string => {
    if (a.data_type === 'ENUM') {
      const vals = Array.isArray(a.allowed_values) ? a.allowed_values : [];
      return vals.length ? vals.join(', ') : t('common.none', 'None');
    }
    return a.regex ?? t('common.none', 'None');
  };

  const rangeText = (a: AttrRow): string => {
    const lo = isNumericType(a.data_type) ? a.min_value : a.min_len;
    const hi = isNumericType(a.data_type) ? a.max_value : a.max_len;
    if (lo == null && hi == null) return t('common.none', 'None');
    return `${lo ?? '—'} … ${hi ?? '—'}`;
  };

  // -------------------------------------------------------------------------
  // Entity list view
  // -------------------------------------------------------------------------
  if (!selectedEntity) {
    const entFacets: { key: EntityFacet; label: string }[] = [
      { key: 'all', label: t('mobile.dse.all', 'All') },
      { key: 'enabled', label: t('mobile.dse.enabled', 'Enabled') },
      { key: 'disabled', label: t('mobile.dse.disabled', 'Disabled') },
    ];

    return (
      <section className="m-screen">
        <MobilePageHeader
          title={t('mobile.dse.title', 'Shadow Fields')}
          description={t('mobile.dse.description', 'Entities extended with custom shadow-field attributes.')}
        />

        <MobileStatStrip
          stats={[
            { value: entities.length, label: t('mobile.dse.stat.total', 'Total') },
            { value: entities.filter((e) => e.is_enabled === 'Y').length, label: t('mobile.dse.stat.enabled', 'Enabled') },
          ]}
        />

        <div className="m-searchbox">
          <Search />
          <input
            value={entFilter}
            onChange={(e) => setEntFilter(e.target.value)}
            placeholder={t('mobile.dse.search_entities', 'Search entities')}
            aria-label={t('mobile.dse.search_entities', 'Search entities')}
          />
        </div>

        <MobileFilterChips
          chips={entFacets.map((f) => ({ value: f.key, label: f.label }))}
          isActive={(v) => entFacet === v}
          onSelect={(v) => setEntFacet(v as EntityFacet)}
        />

        <div className="m-actionbar">
          <button type="button" className="m-btn m-btn-primary" onClick={() => setEnableOpen(true)}>
            <Plus size={16} />
            {t('mobile.dse.enable_entity', 'Enable an entity')}
          </button>
        </div>

        {error && (
          <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>
        )}

        {filteredEntities.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dse.no_entities', 'No entities enabled')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredEntities.map((e) => (
              <MobileCardRow
                key={e.dse_entity_id}
                lead={<Database />}
                leadTone={e.is_enabled === 'Y' ? 'blue' : 'slate'}
                title={e.entity_name}
                subtitle={`${policyLabel(t, e.unknown_key_policy)} · ${t('mobile.dse.fields_count', { count: e.field_count, defaultValue: `${e.field_count} fields` })}`}
                trailing={
                  <MobileStatusPill tone={e.is_enabled === 'Y' ? 'ok' : 'no'}>
                    {e.is_enabled === 'Y' ? t('mobile.dse.on', 'On') : t('mobile.dse.off', 'Off')}
                  </MobileStatusPill>
                }
                chevron
                onClick={() => openEntity(e.entity_name)}
              />
            ))}
          </MobileContentCard>
        )}

        <MobileDseEnableSheet
          open={enableOpen}
          onClose={() => setEnableOpen(false)}
          onEnabled={(name) => { setEnableOpen(false); void reloadEntities(); openEntity(name); }}
        />
      </section>
    );
  }

  // -------------------------------------------------------------------------
  // Attribute list view (a single entity's ext_attributes schema)
  // -------------------------------------------------------------------------
  const attrFacets: { key: AttrFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dse.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const INDEXED_CHIP = '__indexed';

  return (
    <section className="m-screen">
      <div className="m-chips">
        <button type="button" className="m-chip" onClick={backToEntities}>
          <ChevronLeft size={14} /> {t('mobile.dse.entities', 'Entities')}
        </button>
      </div>

      <MobilePageHeader
        title={selectedEntity}
        description={t('mobile.dse.attr_description', 'Custom shadow-field attributes defined on this entity.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.fields, label: t('mobile.dse.kpi.fields', 'Fields') },
          { value: kpis.active, label: t('mobile.dse.kpi.active', 'Active') },
          { value: kpis.indexed, label: t('mobile.dse.kpi.indexed', 'Indexed') },
          { value: kpis.required, label: t('mobile.dse.kpi.required', 'Required') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={attrFilter}
          onChange={(e) => setAttrFilter(e.target.value)}
          placeholder={t('mobile.dse.search_fields', 'Search fields in {{entity}}', { entity: selectedEntity })}
          aria-label={t('mobile.dse.search_fields', 'Search fields in {{entity}}', { entity: selectedEntity })}
        />
      </div>

      <MobileFilterChips
        chips={[
          ...attrFacets.map((f) => ({ value: f.key, label: f.label })),
          { value: INDEXED_CHIP, label: t('mobile.dse.indexed', 'Indexed') },
        ]}
        isActive={(v) => (v === INDEXED_CHIP ? indexedOnly : attrFacet === v)}
        onSelect={(v) => { if (v === INDEXED_CHIP) setIndexedOnly((p) => !p); else setAttrFacet(v as AttrFacet); }}
      />

      <div className="m-actionbar">
        <button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => void addField()}>
          <Plus size={16} />
          {t('mobile.dse.add_field', 'Add field')}
        </button>
        <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => void rebuild()}>
          <RefreshCw size={16} />
          {t('mobile.dse.rebuild_view', 'Rebuild view')}
        </button>
      </div>

      {error && (
        <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>
      )}

      {filteredAttrs.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dse.no_fields', 'No custom fields defined')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filteredAttrs.map((a) => (
            <MobileCardRow
              key={a.dse_attribute_def_id}
              lead={<KeyRound />}
              leadTone={leadForType(a.data_type)}
              title={a.label?.trim() || a.attribute_key}
              subtitle={`${a.attribute_key} · ${typeLabel(t, a.data_type)}${a.is_required === 'Y' ? ' · ' + t('mobile.dse.required', 'Required') : ''}`}
              trailing={
                <MobileStatusPill tone={a.is_active === 'Y' ? 'ok' : 'no'}>
                  {a.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelectedAttr(a)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Attribute detail bottom-sheet */}
      <div className={'m-scrim' + (selectedAttr ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelectedAttr(null); }} />
      <div className={'m-sheet' + (selectedAttr ? ' m-open' : '')} aria-label={t('mobile.dse.field_detail', 'Field detail')}>
        <div className="m-grabber" />
        {selectedAttr && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KeyRound size={20} /></span>
              <span>
                <div className="m-nm">{selectedAttr.label?.trim() || selectedAttr.attribute_key}</div>
                <div className="m-em">{selectedAttr.attribute_key}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dse.f.type', 'Type')} value={typeLabel(t, selectedAttr.data_type)} />
              <MobileDetailRow
                label={t('mobile.dse.f.required', 'Required')}
                value={
                  <MobileStatusPill tone={selectedAttr.is_required === 'Y' ? 'ok' : 'no'}>
                    {selectedAttr.is_required === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.dse.f.indexed', 'Indexed')}
                value={
                  <MobileStatusPill tone={selectedAttr.is_indexed === 'Y' ? 'ok' : 'no'}>
                    {selectedAttr.is_indexed === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.dse.f.format', 'Format')} value={selectedAttr.format_name?.trim() || t('common.none', 'None')} />
              <MobileDetailRow
                label={selectedAttr.data_type === 'ENUM' ? t('mobile.dse.f.allowed', 'Allowed values') : t('mobile.dse.f.regex', 'Pattern')}
                value={constraintText(selectedAttr)}
              />
              <MobileDetailRow label={t('mobile.dse.f.range', 'Range')} value={rangeText(selectedAttr)} />
              <MobileDetailRow label={t('mobile.dse.f.sort', 'Sort order')} value={selectedAttr.sort_order} />
              <MobileDetailRow
                label={t('mobile.dse.f.status', 'Status')}
                value={
                  <MobileStatusPill tone={selectedAttr.is_active === 'Y' ? 'ok' : 'no'}>
                    {selectedAttr.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selectedAttr); setSelectedAttr(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.dse.edit', 'Edit')}
              </button>)}
              <button
                type="button"
                className="m-btn m-btn-danger"
                disabled={actionBusy}
                onClick={() => void deleteAttr(selectedAttr)}
              >
                <Trash2 size={16} />
                {t('mobile.dse.delete', 'Delete')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelectedAttr(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileDseAttrEditSheet
        session={session}
        attr={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); if (selectedEntity) void reloadAttrs(selectedEntity); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Enable-entity sheet — creates the JSONB shadow-field surface on a table via
// the sanctioned dse_enable_entity RPC (adds ext_attributes + GIN index). This
// is a schema-management RPC, not a business-row write, mirroring the desktop.
// ---------------------------------------------------------------------------

interface MobileDseEnableSheetProps {
  open: boolean;
  onClose: () => void;
  onEnabled: (entityName: string) => void;
}

function MobileDseEnableSheet({ open, onClose, onEnabled }: MobileDseEnableSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [policy, setPolicy] = useState<Policy>('ALLOW');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setName(''); setPolicy('ALLOW'); setError(null); }
  }, [open]);

  const enable = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError(t('mobile.dse.entity_required', 'Enter a table name.')); return; }
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'dse_enable_entity',
        { p_entity: trimmed, p_policy: policy },
      );
      if (e) throw e;
      if (data && data.ok === false) {
        setError(data.error_code ?? t('mobile.dse.enable_failed', 'Enable failed.'));
        return;
      }
      onEnabled(trimmed);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.dse.enable_entity', 'Enable an entity')}>
        <div className="m-grabber" />
        {open && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dse.enable_entity', 'Enable an entity')}</div>
                <div className="m-em">{t('mobile.dse.enable_hint', 'Adds shadow-field storage to a table')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-dse-entity">{t('mobile.dse.table_name', 'Table name')}</label>
                <input
                  id="m-dse-entity"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('mobile.dse.table_name_ph', 'table_name')}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-policy">{t('mobile.dse.unknown_policy', 'Unknown-key policy')}</label>
                <select
                  id="m-dse-policy"
                  className="m-sel"
                  value={policy}
                  onChange={(e) => setPolicy(e.target.value as Policy)}
                >
                  {POLICIES.map((p) => <option key={p.value} value={p.value}>{t(p.labelKey, p.fallback)}</option>)}
                </select>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void enable()}>
                {busy ? t('common.saving', 'Saving…') : t('mobile.dse.enable', 'Enable')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Attribute edit sheet — the per-key schema the Common Routine validates on every
// write. Saved through routedUpdate (§2). Mirrors the desktop saveAttr payload.
// ---------------------------------------------------------------------------

interface MobileDseAttrEditSheetProps {
  session: AuthSession;
  attr: AttrRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileDseAttrEditSheet({ session, attr, onClose, onSaved }: MobileDseAttrEditSheetProps) {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [dataType, setDataType] = useState<DataType>('STRING');
  const [formatName, setFormatName] = useState('');
  const [constraint, setConstraint] = useState('');
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');
  const [sortText, setSortText] = useState('0');
  const [required, setRequired] = useState(false);
  const [indexed, setIndexed] = useState(false);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different attribute is opened.
  useEffect(() => {
    if (!attr) return;
    setKey(attr.attribute_key);
    setLabel(attr.label ?? '');
    setDataType(attr.data_type);
    setFormatName(attr.format_name ?? '');
    setConstraint(
      attr.data_type === 'ENUM'
        ? JSON.stringify(attr.allowed_values ?? [])
        : (attr.regex ?? ''),
    );
    const lo = isNumericType(attr.data_type) ? attr.min_value : attr.min_len;
    const hi = isNumericType(attr.data_type) ? attr.max_value : attr.max_len;
    setMinText(lo == null ? '' : String(lo));
    setMaxText(hi == null ? '' : String(hi));
    setSortText(String(attr.sort_order ?? 0));
    setRequired(attr.is_required === 'Y');
    setIndexed(attr.is_indexed === 'Y');
    setActive(attr.is_active === 'Y');
    setError(null);
  }, [attr]);

  const numOrNull = (text: string): number | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    if (!attr) return;
    const trimmedKey = key.trim();
    if (!trimmedKey) { setError(t('mobile.dse.key_required', 'Attribute key is required.')); return; }

    let allowed: unknown = null;
    if (dataType === 'ENUM' && constraint.trim()) {
      try {
        allowed = JSON.parse(constraint);
      } catch {
        setError(t('mobile.dse.allowed_json', 'Allowed values must be a JSON array.'));
        return;
      }
    }

    const numeric = isNumericType(dataType);
    const lo = numOrNull(minText);
    const hi = numOrNull(maxText);

    const patch: Record<string, unknown> = {
      attribute_key: trimmedKey,
      label: label.trim() || null,
      data_type: dataType,
      is_required: required ? 'Y' : 'N',
      format_name: formatName.trim() || null,
      regex: dataType === 'ENUM' ? null : (constraint.trim() || null),
      allowed_values: allowed,
      min_value: numeric ? lo : null,
      max_value: numeric ? hi : null,
      min_len: numeric ? null : lo,
      max_len: numeric ? null : hi,
      is_indexed: indexed ? 'Y' : 'N',
      sort_order: numOrNull(sortText) ?? 0,
      is_active: active ? 'Y' : 'N',
    };

    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dse_attribute_def', 'dse_attribute_def_id', attr.dse_attribute_def_id, patch);
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = attr !== null;
  const numeric = isNumericType(dataType);

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.dse.edit_field', 'Edit field')}>
        <div className="m-grabber" />
        {attr && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KeyRound size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dse.edit_field', 'Edit field')}</div>
                <div className="m-em">{attr.entity_name}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-dse-key">{t('mobile.dse.f.key', 'Attribute key')}</label>
                <input id="m-dse-key" className="m-inp" value={key} onChange={(e) => setKey(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-label">{t('mobile.dse.f.label', 'Label')}</label>
                <input id="m-dse-label" className="m-inp" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-type">{t('mobile.dse.f.type', 'Type')}</label>
                <select id="m-dse-type" className="m-sel" value={dataType} onChange={(e) => setDataType(e.target.value as DataType)}>
                  {DATA_TYPES.map((dt) => <option key={dt.value} value={dt.value}>{t(dt.labelKey, dt.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-format">{t('mobile.dse.f.format', 'Format')}</label>
                <input
                  id="m-dse-format"
                  className="m-inp"
                  value={formatName}
                  onChange={(e) => setFormatName(e.target.value)}
                  placeholder={t('mobile.dse.format_ph', 'ISO_DATE')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-constraint">
                  {dataType === 'ENUM' ? t('mobile.dse.f.allowed', 'Allowed values') : t('mobile.dse.f.regex', 'Pattern')}
                </label>
                <input
                  id="m-dse-constraint"
                  className="m-inp"
                  value={constraint}
                  onChange={(e) => setConstraint(e.target.value)}
                  placeholder={dataType === 'ENUM' ? '["A","B"]' : t('mobile.dse.regex_ph', 'regex')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-min">{numeric ? t('mobile.dse.f.min_value', 'Min value') : t('mobile.dse.f.min_len', 'Min length')}</label>
                <input id="m-dse-min" className="m-inp" type="number" value={minText} onChange={(e) => setMinText(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-max">{numeric ? t('mobile.dse.f.max_value', 'Max value') : t('mobile.dse.f.max_len', 'Max length')}</label>
                <input id="m-dse-max" className="m-inp" type="number" value={maxText} onChange={(e) => setMaxText(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dse-sort">{t('mobile.dse.f.sort', 'Sort order')}</label>
                <input id="m-dse-sort" className="m-inp" type="number" value={sortText} onChange={(e) => setSortText(e.target.value)} />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.dse.f.required', 'Required')}</div>
                  <div className="m-formrow-s">{t('mobile.dse.required_hint', 'Rejected on write when missing')}</div>
                </span>
                <button type="button" role="switch" aria-checked={required} className={'m-switch ' + (required ? 'm-on' : 'm-off')} onClick={() => setRequired((v) => !v)}>
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.dse.f.indexed', 'Indexed')}</div>
                  <div className="m-formrow-s">{t('mobile.dse.indexed_hint', 'Builds a functional index on rebuild')}</div>
                </span>
                <button type="button" role="switch" aria-checked={indexed} className={'m-switch ' + (indexed ? 'm-on' : 'm-off')} onClick={() => setIndexed((v) => !v)}>
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.dse.active_hint', 'Included in the flat view')}</div>
                </span>
                <button type="button" role="switch" aria-checked={active} className={'m-switch ' + (active ? 'm-on' : 'm-off')} onClick={() => setActive((v) => !v)}>
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
